import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { useIntl } from "@ai360/core/intl";
import { HierarchyAPI, SteeringDataAPI } from "@ai360/core";
import { getTheUserGuid } from "~/login/selectors";

import {
    Section,
    SubSection,
    TextInput,
    TextArea,
    RadioButtonGroup,
    RadioButton,
    Loader,
    LoaderTypes,
} from "~/core";

import {
    DragAndDropFileUploader,
    STEERING_DATA_UPLOADER_STATE_KEY,
    actions as uploaderActions,
    selectors as uploaderSelectors,
    models as uploaderModels,
} from "~/action-panel/components/common/drag-and-drop-file-uploader";

import { OrgLevelList } from "../org-level-products/add-edit/components/org-level-list";

import { steeringDataActions, steeringDataSelectors } from "./data";
import { messages } from "./i18n-messages";

import "./steering-data-form.css";
import "../add-edit-shared-responsive.css";

// --- Constants ---

const SUPPORTED_EXTENSIONS = [
    ".pdf",
    ".doc",
    ".docx",
    ".txt",
    ".csv",
    ".xls",
    ".xlsx",
    ".pptx",
    ".zip",
    ".jpg",
    ".jpeg",
    ".png",
];

const NAME_MAX_LENGTH = 255;
const NOTES_MAX_LENGTH = 2000;
const URL_MAX_LENGTH = 2048;

type SourceType = "file" | "url";

type IHierarchyFilterLevelWithParentsGuid = HierarchyAPI.IHierarchyFilterLevelWithParentsGuid;

// --- URL Validation ---

const PRIVATE_URL_PATTERNS = [/sharepoint\.com/i, /atlassian\.net/i, /confluence/i, /jira/i];

const PRIVATE_IP_PATTERNS = [
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}/,
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}/,
    /^192\.168\.\d{1,3}\.\d{1,3}/,
];

function isWellFormedUrl(value: string): boolean {
    if (!value || value.length > URL_MAX_LENGTH) {
        return false;
    }
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

function isPrivateUrl(value: string): boolean {
    try {
        const url = new URL(value);
        const hostname = url.hostname;

        // Check hostname patterns
        for (const pattern of PRIVATE_URL_PATTERNS) {
            if (pattern.test(hostname)) {
                return true;
            }
        }

        // Check private IP patterns
        for (const pattern of PRIVATE_IP_PATTERNS) {
            if (pattern.test(hostname)) {
                return true;
            }
        }

        return false;
    } catch {
        return false;
    }
}

function validateUrl(value: string): string | null {
    if (!value) {
        return "Public URL is required.";
    }
    if (value.length > URL_MAX_LENGTH) {
        return `URL must not exceed ${URL_MAX_LENGTH} characters.`;
    }
    if (!isWellFormedUrl(value)) {
        return "Please enter a valid URL with http:// or https:// scheme.";
    }
    if (isPrivateUrl(value)) {
        return "Only public URLs are supported. Private URLs (SharePoint, Jira, Confluence, internal IPs) are not allowed.";
    }
    return null;
}

// --- Component Props ---

interface ISteeringDataFormProps {
    closePanel: () => void;
    selectedRecord: SteeringDataAPI.ISteeringDataRecord | null;
}

// --- Component ---

export const SteeringDataForm = ({ closePanel, selectedRecord }: ISteeringDataFormProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const userGuid = useAppSelector(getTheUserGuid);
    const loading = useAppSelector(steeringDataSelectors.getSteeringDataLoading);

    // Files selected via the shared drag-and-drop uploader (same component as Rx File Import).
    const uploaderState = useAppSelector((state) =>
        uploaderSelectors.getModuleState(state, STEERING_DATA_UPLOADER_STATE_KEY)
    );

    // Top-level entries the user added (ZIPs appear here with their children expanded below).
    const topLevelEntries: uploaderModels.UploadFileInfo[] = React.useMemo(() => {
        if (!uploaderState?.uploadFileInfoMap) return [];
        return Array.from(uploaderState.uploadFileInfoMap.values()).filter(
            (ufi: uploaderModels.UploadFileInfo) => ufi.parent == null
        );
    }, [uploaderState?.uploadFileInfoMap]);

    // The actual leaf documents we'll process — one record per leaf. A ZIP contributes
    // only the child entries the user kept (removing a child drops it from parent.children),
    // NOT the zip itself. Plain files contribute themselves.
    const selectedLeaves: uploaderModels.UploadFileInfo[] = React.useMemo(() => {
        const leaves: uploaderModels.UploadFileInfo[] = [];
        for (const entry of topLevelEntries) {
            if (entry.isZipFile()) {
                for (const child of entry.children) {
                    leaves.push(child);
                }
            } else {
                leaves.push(entry);
            }
        }
        return leaves;
    }, [topLevelEntries]);

    const selectedFileCount = selectedLeaves.length;
    const hasFileErrors = (uploaderState?.filesWithErrorsCount ?? 0) > 0;

    // Resolve the selected leaves into real File objects. ZIP children are extracted
    // client-side from the parent ZIP blob so only the kept files are uploaded — the
    // ZIP itself is never sent to the backend.
    const resolveSelectedFiles = useCallback(async (): Promise<File[]> => {
        const zip = await import("@zip.js/zip.js");
        const files: File[] = [];

        for (const entry of topLevelEntries) {
            if (!entry.isZipFile()) {
                if (entry.fileObj) {
                    files.push(entry.fileObj);
                }
                continue;
            }

            const keptChildren = entry.children;
            if (keptChildren.length === 0 || !entry.fileObj) {
                continue;
            }

            // Map of the archive paths the user kept
            const keptByPath = new Map<string, uploaderModels.UploadFileInfo>();
            for (const child of keptChildren) {
                if (child.archiveAbsFileName) {
                    keptByPath.set(child.archiveAbsFileName, child);
                }
            }

            let reader: any;
            try {
                reader = new zip.ZipReader(new zip.BlobReader(entry.fileObj));
                const zipEntries = await reader.getEntries();
                for (const zipEntry of zipEntries) {
                    if (zipEntry.directory) continue;
                    const kept = keptByPath.get(zipEntry.filename);
                    if (!kept || !zipEntry.getData) continue;
                    const blob = await zipEntry.getData(new zip.BlobWriter());
                    const fileName = kept.fileName; // basename, already computed by the uploader
                    files.push(new File([blob], fileName));
                }
            } catch (err) {
                console.error("Failed to extract selected files from ZIP:", err);
            } finally {
                try {
                    await reader?.close();
                } catch {
                    // ignore
                }
            }
        }

        return files;
    }, [topLevelEntries]);

    const isEditMode = !!selectedRecord;

    // --- Form State ---

    const [name, setName] = useState<string>(selectedRecord?.name || "");
    const [sourceType, setSourceType] = useState<SourceType>(selectedRecord?.sourceType || "file");
    const [publicUrl, setPublicUrl] = useState<string>(selectedRecord?.publicUrl || "");
    const [notes, setNotes] = useState<string>(selectedRecord?.notes || "");

    // Hierarchy levels
    const [orgLevels, setOrgLevels] = useState<IHierarchyFilterLevelWithParentsGuid[]>([]);
    const [selectedOrgLevels, setSelectedOrgLevels] = useState<
        IHierarchyFilterLevelWithParentsGuid[]
    >([]);
    const [orgLevelsLoading, setOrgLevelsLoading] = useState<boolean>(true);

    // Validation errors
    const [nameError, setNameError] = useState<string | null>(null);
    const [urlError, setUrlError] = useState<string | null>(null);
    const [hierarchyError, setHierarchyError] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

    // Track submission state
    const [_isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Container that the shared uploader attaches its drop listeners to.
    // Use a callback ref so we re-capture the node when the file section mounts
    // (e.g. after toggling from URL back to File source).
    const [dragAndDropNode, setDragAndDropNode] = useState<HTMLElement | null>(null);
    const dndContainerRef = useCallback((node: HTMLDivElement | null) => {
        setDragAndDropNode(node);
    }, []);

    // --- Configure the shared drag-and-drop uploader ---

    useEffect(() => {
        // Set valid extensions and start from an empty list each time the form opens.
        dispatch(
            uploaderActions.setValidExtensions(
                STEERING_DATA_UPLOADER_STATE_KEY,
                SUPPORTED_EXTENSIONS
            )
        );
        dispatch(uploaderActions.clearUploadFileList(STEERING_DATA_UPLOADER_STATE_KEY));
        return () => {
            // Clean up selected files when the form unmounts.
            dispatch(uploaderActions.clearUploadFileList(STEERING_DATA_UPLOADER_STATE_KEY));
        };
    }, [dispatch]);

    // --- Load Org Levels ---

    useEffect(() => {
        let cancelled = false;

        const loadOrgLevels = async () => {
            try {
                setOrgLevelsLoading(true);
                const response = await HierarchyAPI.getOrgLevelList(userGuid);
                if (!cancelled) {
                    setOrgLevels(response || []);
                    setOrgLevelsLoading(false);
                }
            } catch {
                if (!cancelled) {
                    setOrgLevels([]);
                    setOrgLevelsLoading(false);
                }
            }
        };

        loadOrgLevels();
        return () => {
            cancelled = true;
        };
    }, [userGuid]);

    // --- Initialize selected org levels from existing record ---

    useEffect(() => {
        if (isEditMode && selectedRecord?.hierarchyLevels && orgLevels.length > 0) {
            const selectedGuids = new Set(
                selectedRecord.hierarchyLevels.map((level) => level.orgLevelGuid)
            );
            const matched = orgLevels.filter((level) => selectedGuids.has(level.orgLevelGuid));
            setSelectedOrgLevels(matched);
        }
    }, [isEditMode, selectedRecord, orgLevels]);

    // --- Validation ---

    const validateForm = useCallback((): boolean => {
        let valid = true;

        // Name is always required. For multi-file uploads it's used as a prefix and
        // each record is named "{name} {index}".
        if (!name.trim()) {
            setNameError("Name is required.");
            valid = false;
        } else if (name.trim().length > NAME_MAX_LENGTH) {
            setNameError(`Name must not exceed ${NAME_MAX_LENGTH} characters.`);
            valid = false;
        } else {
            setNameError(null);
        }

        // Source type validation
        if (sourceType === "url") {
            const urlErr = validateUrl(publicUrl);
            if (urlErr) {
                setUrlError(urlErr);
                valid = false;
            } else {
                setUrlError(null);
            }
        } else {
            setUrlError(null);
            // File validation: at least one file required on add mode
            if (!isEditMode && selectedFileCount === 0) {
                setFileError("Please select at least one file to upload.");
                valid = false;
            } else if (hasFileErrors) {
                // The uploader shows per-file errors inline; block submit until resolved.
                setFileError("Please remove files with errors before submitting.");
                valid = false;
            } else {
                setFileError(null);
            }
        }

        // Hierarchy levels validation
        if (selectedOrgLevels.length === 0) {
            setHierarchyError("At least one hierarchy level must be selected.");
            valid = false;
        } else {
            setHierarchyError(null);
        }

        return valid;
    }, [
        name,
        sourceType,
        publicUrl,
        selectedFileCount,
        hasFileErrors,
        selectedOrgLevels,
        isEditMode,
    ]);

    // --- Submit ---

    const onSubmit = useCallback(async () => {
        setSubmitError(null);
        setSubmitSuccess(false);

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        const hierarchyLevelGuids = selectedOrgLevels.map((level) => level.orgLevelGuid);

        try {
            if (isEditMode) {
                // Edit: single file replacement. Resolve the one selected leaf (extracting
                // from a ZIP if the user picked a single child).
                const resolved = sourceType === "file" ? await resolveSelectedFiles() : [];
                dispatch(
                    steeringDataActions.updateSteeringData({
                        steeringDataGuid: selectedRecord.steeringDataGuid,
                        name: name.trim(),
                        sourceType,
                        publicUrl: sourceType === "url" ? publicUrl : undefined,
                        hierarchyLevelGuids,
                        notes: notes.trim() || undefined,
                        file: sourceType === "file" ? resolved[0] : undefined,
                    })
                );
            } else if (sourceType === "file") {
                // Extract the actual files to process (ZIP children extracted client-side;
                // the ZIP itself is never uploaded).
                const resolved = await resolveSelectedFiles();

                if (resolved.length === 0) {
                    setSubmitError("No files could be prepared for upload. Please try again.");
                    setIsSubmitting(false);
                    return;
                }

                if (resolved.length > 1) {
                    // Multiple files → one record per file, named "{name} {index}"
                    dispatch(
                        steeringDataActions.createSteeringDataBatch({
                            files: resolved,
                            baseName: name.trim(),
                            hierarchyLevelGuids,
                            notes: notes.trim() || undefined,
                        })
                    );
                } else {
                    dispatch(
                        steeringDataActions.createSteeringData({
                            name: name.trim(),
                            sourceType,
                            hierarchyLevelGuids,
                            notes: notes.trim() || undefined,
                            file: resolved[0],
                        })
                    );
                }
            } else {
                // URL source
                dispatch(
                    steeringDataActions.createSteeringData({
                        name: name.trim(),
                        sourceType,
                        publicUrl,
                        hierarchyLevelGuids,
                        notes: notes.trim() || undefined,
                        file: undefined,
                    })
                );
            }

            setSubmitSuccess(true);
            setIsSubmitting(false);

            // Close panel after brief delay to show success
            setTimeout(() => {
                closePanel();
            }, 1000);
        } catch (error) {
            setSubmitError(error?.message || "An error occurred while saving. Please try again.");
            setIsSubmitting(false);
            // Form data is preserved for retry
        }
    }, [
        validateForm,
        isEditMode,
        selectedRecord,
        name,
        sourceType,
        publicUrl,
        notes,
        resolveSelectedFiles,
        selectedOrgLevels,
        dispatch,
        closePanel,
    ]);

    // --- Listen for submit event from SlidingPanel header button ---

    useEffect(() => {
        const handleExternalSubmit = () => {
            onSubmit();
        };
        document.addEventListener("steering-data-form-submit", handleExternalSubmit);
        return () => {
            document.removeEventListener("steering-data-form-submit", handleExternalSubmit);
        };
    }, [onSubmit]);

    // --- Hierarchy Level Selection ---

    const onOrgLevelSelectionChange = useCallback(
        (items: IHierarchyFilterLevelWithParentsGuid[]) => {
            setSelectedOrgLevels(items);
            if (items.length > 0) {
                setHierarchyError(null);
            }
        },
        []
    );

    // --- Render ---

    return (
        <div className="add-edit-panel steering-data-add-edit-panel">
            {loading && <Loader type={LoaderTypes.LINE_SCALE} />}

            {submitSuccess && (
                <div className="steering-data-form-success">
                    {isEditMode
                        ? formatMessage(messages.updateSuccess)
                        : formatMessage(messages.createSuccess)}
                </div>
            )}

            {submitError && <div className="steering-data-form-error">{submitError}</div>}

            <div className="section-container">
                {/* Left Column: Source type toggle, Name, URL/Notes */}
                <Section>
                    <SubSection>
                        <RadioButtonGroup
                            className="steering-data-source-toggle"
                            selectedValue={sourceType}
                            disabled={isEditMode}
                            afterOnChange={(value: string | boolean) => {
                                if (!isEditMode) {
                                    setSourceType(value as SourceType);
                                    setUrlError(null);
                                    setFileError(null);
                                }
                            }}
                        >
                            <RadioButton
                                tabIndex={0}
                                value="file"
                                label={formatMessage(messages.fileUploadLabel)}
                                disabled={isEditMode}
                            />
                            <RadioButton
                                tabIndex={0}
                                value="url"
                                label={formatMessage(messages.publicUrlLabel)}
                                disabled={isEditMode}
                            />
                        </RadioButtonGroup>
                        {isEditMode && (
                            <span className="steering-data-locked-hint">
                                {formatMessage(messages.sourceTypeLocked)}
                            </span>
                        )}
                    </SubSection>
                    <SubSection>
                        <TextInput
                            required
                            autoFocus
                            containerClassNames={["form-long-input"]}
                            maxLength={NAME_MAX_LENGTH}
                            placeholderText={formatMessage(messages.nameLabel)}
                            value={name}
                            onChange={(value: string) => {
                                setName(value);
                                if (nameError) setNameError(null);
                            }}
                            hasError={!!nameError}
                            errorText={nameError}
                        />
                    </SubSection>
                    {sourceType === "url" && (
                        <SubSection>
                            <TextInput
                                required
                                containerClassNames={["form-long-input"]}
                                maxLength={URL_MAX_LENGTH}
                                placeholderText={formatMessage(messages.publicUrlLabel)}
                                value={publicUrl}
                                onChange={(value: string) => {
                                    setPublicUrl(value);
                                    if (urlError) setUrlError(null);
                                }}
                                hasError={!!urlError}
                                errorText={urlError}
                            />
                        </SubSection>
                    )}
                    <SubSection>
                        <TextArea
                            maxLength={NOTES_MAX_LENGTH}
                            placeholderText={formatMessage(messages.notesLabel)}
                            value={notes}
                            onChange={(value: string) => setNotes(value)}
                        />
                    </SubSection>
                </Section>

                <span className="bar section-fence" />

                {/* Right Column: Location Affiliations */}
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.hierarchyLevelsLabel)}
                >
                    {orgLevelsLoading ? (
                        <Loader type={LoaderTypes.LINE_SCALE} />
                    ) : (
                        <OrgLevelList
                            orgLevels={orgLevels}
                            selectedOrgLevels={selectedOrgLevels}
                            onSelectionChange={onOrgLevelSelectionChange}
                        />
                    )}
                    {hierarchyError && (
                        <div className="steering-data-field-error">{hierarchyError}</div>
                    )}
                </Section>
            </div>

            {/* File Upload Section - below section-container (file mode only).
                Reuses the shared DragAndDropFileUploader (same component as Rx File Import
                and the Upload Files modal) for a consistent look before/after selection. */}
            {sourceType === "file" && (
                <div className="steering-data-file-section" ref={dndContainerRef}>
                    <DragAndDropFileUploader
                        stateKey={STEERING_DATA_UPLOADER_STATE_KEY}
                        dragAndDropNode={dragAndDropNode}
                    />
                    {fileError && <div className="steering-data-field-error">{fileError}</div>}
                    {isEditMode && selectedRecord?.fileName && selectedFileCount === 0 && (
                        <div className="steering-data-current-file">
                            {formatMessage(messages.currentFile, {
                                fileName: selectedRecord.fileName,
                            })}
                        </div>
                    )}
                    <div className="steering-data-supported-badge">
                        Supported File Types:
                        <br />
                        .pdf, .doc, .docx, .pptx, .xls, .xlsx, .txt, .csv, .zip, .jpg, .png
                    </div>
                </div>
            )}
        </div>
    );
};

export default SteeringDataForm;
